// 레이드 1단계(docs/RAID-PRD.md): 잿불 비룡 두뇌·AI 용병 3·위협·불 장판. 영웅 1 + 용병 3이 같은 경고로 맞는다(경고 = 판정).
// 보스 상태 뷰(g.boss)는 기존 보스와 같이 쓰고(HUD·영웅 공격·자석이 그대로 동작), 행동만 여기서 정한다(BossController가 종류로 넘긴다).
// 시간: 세계 영역. 위치는 dt × 세계 배율로 적분하고, 정수 타이머는 세계 정수 틱에서만 센다(boss.ts와 같은 규칙). 프레임 안 할당 없음.
import { FIXED_DT, deg } from "../shared/config";
import { Ev, HitFlag, TeleSource } from "../shared/events";
import { BossKind, BossState, Scene, TeleShape, type BossStateId, type TeleShapeId } from "../shared/types";
import {
  HEAL, MERCS, MERC_COUNT, MERC_REVIVE, PARTY_SIZE, TAUNT, WYVERN, WyvernPattern, mercStats, pickTarget, raidTuning, type RaidDiff, type RaidId, type RaidTuning,
} from "../shared/content/raid";
import { Rng } from "../core/rng";
import { telegraphHit } from "./combat";
import type { GameSim } from "./game";
import { TIMER_MAX } from "./state";

/** 용병 동작(렌더·HUD): 서기·걷기·공격·시전(치유·도발)·쓰러짐 */
export const MercAct = { Idle: 0, Move: 1, Attack: 2, Cast: 3, Down: 4 } as const;
/** Ev.RaidMerc b 값 */
export const MercEvent = { Attack: 0, Heal: 1, Taunt: 2, Down: 3, Revive: 4, Hurt: 5, Dodge: 6 } as const;

export class MercState {
  x = 0; z = 0; prevX = 0; prevZ = 0; facing = Math.PI;
  hp = 1; maxHp = 1; attack = 1;
  act: number = MercAct.Idle; actTick = 0;
  /** 쓰러진 뒤 다시 일어나기까지 남은 세계 틱(0 = 살아 있음) */
  down = 0;
  /** 다음 공격·기술(도발·치유)까지 남은 세계 틱 */
  hitClock = 0; skillClock = 0;
  /** 누적 위협 */
  threat = 0;
  /** 피하기: 반응해 움직이기 시작할 세계 틱(-1 = 예정 없음), 피하는 중인지, 목표점 */
  dodgeAt = -1; evading = false; evX = 0; evZ = 0;
  /** 마지막 피격 뒤 세계 틱(렌더 깜빡임) */
  hurtTick = 0xffff;
  /** 넷 판: 이 용병 칸이 쓰이는가(사람이 차지한 칸은 false — 렌더·HUD가 숨긴다). 솔로는 늘 true */
  visible = true;
  get alive(): boolean { return this.down === 0 && this.hp > 0; }
}

/** 불 장판(화염 장판이 남긴 자리) */
export class FirePool {
  active = false; x = 0; z = 0; radius = 0; ttl = 0; total = 1; clock = 0;
}

/** 레이드 뷰(렌더·UI 읽기 전용) */
export class RaidState {
  /** 레이드 판인지 */
  active = false;
  raid: RaidId | "" = "";
  /** 보스가 노리는 파티 칸(0 영웅, 1..3 용병, -1 없음) */
  target = -1;
  /** 도발 남은 세계 틱 */
  tauntTicks = 0;
  /** 페이즈(1·2), 광폭, 비행 높이 비율(0..1), 지금 패턴(WyvernPattern, -1 없음) */
  phase = 1; enraged = false; air = 0; pattern = -1;
  /** 광폭까지 남은 세계 틱(등장 끝부터 센다) */
  enrageIn: number = WYVERN.enrage.at;
  /** 이번 주 받은 보상 횟수·한도(입장 때 정한다), 연습 판인지(보상 다 받음) */
  weekClears = 0; weekLimit = 3; practice = false;
  /** 난이도(C13)·하드 해금·하드 첫 클리어(칭호). 해금·첫 클리어는 마을에서 flow.refreshRaidWeek가 채운다 */
  diff: RaidDiff = "normal"; hardOpen = false; hardFirst = false;
  /** 하드 잿불 낙인: 붙은 파티 칸 비트(1 << 칸)와 폭발까지 남은 세계 틱(0 = 없음) — HUD·VFX·소리가 읽는다 */
  brandSlots = 0; brandLeft = 0;
  readonly mercs: MercState[] = MERCS.map(() => new MercState());
  readonly fires: FirePool[] = Array.from({ length: WYVERN.fire.max }, () => new FirePool());
}

/** 패턴 진행 중인 경고 슬롯 최대 수(화염 장판 페이즈 2 = 6) */
const SLOTS = 8;
const ARENA_PAD = 1.2;
const SAFE_SAMPLES = 16;
const SAFE_RINGS = [3.4, 4.6, 6.2] as const;

export class RaidController {
  readonly state = new RaidState();
  private readonly rng: Rng;
  /** 보스 두뇌 */
  private readonly slots = new Int16Array(SLOTS).fill(-1);
  private readonly due = new Int32Array(SLOTS);
  private readonly born = new Int32Array(SLOTS);
  private readonly mult = new Float32Array(SLOTS);
  private slotCount = 0;
  private resolved = 0;
  /** 이번 패턴에 이미 맞은 파티 칸(비트) */
  private hitMask = 0;
  private ptick = 0;
  private cooldown = 0;
  private attacks = 0;
  private lastPattern = -1;
  private retarget = 0;
  private staggerAcc = 0;
  private flightClock = 0;
  private groundLeft = 0;
  private dashAngle = 0;
  private dashLeft = 0;
  private landX = 0;
  private landZ = 0;
  /** 비행 단계: 0 지상, 1 이륙, 2 브레스, 3 착지 경고 */
  private flight = 0;
  private breathMade = 0;
  /** 파티 칸 살아 있음·위협(pickTarget 입력, 할당 없이 재사용) */
  private readonly alive = [false, false, false, false];
  private readonly threat = new Float64Array(PARTY_SIZE);
  /** 무대 중심·반경(입장 때 방 영역에서) */
  private cx = 0; private cz = 0; private cr = 18;
  private tauntBy = -1;
  /** 판 수치(난이도 반영, prepare가 정한다) */
  private tune: RaidTuning = raidTuning("raid_wyvern", "normal");
  /** 잿불 낙인 경고 k의 주인 파티 칸(-1 없음) */
  private readonly brandWho = new Int8Array(SLOTS).fill(-1);
  /** 같은 틱 낙인 폭발의 영웅 피해 합(한 번에 준다) */
  private brandHeroDmg = 0;
  private brandHeroHeavy = false;
  private brandHeroX = 0;
  private brandHeroZ = 0;
  /** 넷 모드(raidLink): 보스·용병·불 장판은 서버 꼭두각시다 — 로컬 두뇌·위협·경직·쓰러짐 판정은 돌지 않는다. prepare/reset이 끈다 */
  netMode = false;

  constructor(private readonly g: GameSim, seed: number) { this.rng = new Rng((seed ^ 0x52414944) >>> 0); }

  get active(): boolean { return this.state.active; }
  /** 보스가 공중에 있다(피해를 받지 않는다) */
  get airborne(): boolean { return this.state.air > 0; }

  reset(): void {
    const s = this.state;
    this.cancelPattern();
    s.active = false; s.raid = ""; s.target = -1; s.tauntTicks = 0; s.phase = 1; s.enraged = false; s.air = 0; s.pattern = -1;
    s.enrageIn = WYVERN.enrage.at; s.diff = "normal"; s.brandSlots = 0; s.brandLeft = 0;
    this.tune = raidTuning("raid_wyvern", "normal");
    this.netMode = false;
    for (const f of s.fires) f.active = false;
    for (const m of s.mercs) { m.visible = true; m.down = 0; m.hp = m.maxHp; m.threat = 0; m.act = MercAct.Idle; m.evading = false; m.dodgeAt = -1; }
    this.threat.fill(0);
    this.ptick = 0; this.cooldown = 0; this.attacks = 0; this.lastPattern = -1; this.retarget = 0; this.staggerAcc = 0;
    this.flightClock = 0; this.groundLeft = 0; this.dashLeft = 0; this.flight = 0; this.breathMade = 0; this.tauntBy = -1;
  }

  /**
   * 레이드 판 준비(loadMap): 용병을 영웅 뒤에 세우고 영웅 레벨로 능력치를 정한다. 주간 보상 횟수는 호출자가 정한다
   */
  prepare(raid: RaidId, weekClears: number, weekLimit: number, diff: RaidDiff = "normal"): void {
    this.reset();
    const s = this.state, g = this.g, h = g.hero;
    s.active = true; s.raid = raid; s.weekClears = weekClears; s.weekLimit = weekLimit; s.practice = weekClears >= weekLimit;
    this.tune = raidTuning(raid, diff); s.diff = this.tune.brand ? diff : "normal"; s.enrageIn = this.tune.enrageAt;
    const level = g.growth.level;
    for (let i = 0; i < MERC_COUNT; i++) {
      const m = s.mercs[i], d = MERCS[i], st = mercStats(d, level);
      m.maxHp = m.hp = st.maxHp; m.attack = st.attack;
      // 영웅 뒤 좌·중·우(다리 위)
      const ox = (i - 1) * 1.4, oz = 2.2 + (i === 1 ? 0.8 : 0);
      m.x = m.prevX = h.x + ox; m.z = m.prevZ = h.z + oz; m.facing = h.facing;
      m.hitClock = 30 + i * 17; m.skillClock = d.role === "tank" ? 120 : d.role === "priest" ? HEAL.every : 0;
      m.act = MercAct.Idle; m.actTick = 0; m.down = 0; m.threat = 0; m.dodgeAt = -1; m.evading = false; m.hurtTick = 0xffff;
    }
  }

  /** 보스방 입장(flow.enterRoom): 무대 중심·반경과 비룡 등장 */
  enterBoss(x: number, z: number, area: { x0: number; z0: number; x1: number; z1: number }): void {
    const g = this.g, b = g.boss;
    this.cx = (area.x0 + area.x1) / 2; this.cz = (area.z0 + area.z1) / 2;
    this.cr = Math.min(area.x1 - area.x0, area.z1 - area.z0) / 2;
    g.bossController.reset();
    b.kind = BossKind.Wyvern; b.variant = 0; b.name = WYVERN.name; b.radius = WYVERN.radius;
    b.x = b.prevX = x; b.z = b.prevZ = z; b.facing = Math.PI * 0;
    b.facing = Math.atan2(g.hero.x - x, g.hero.z - z);
    b.hp = b.maxHp = g.flowController.plan.bossHp > 0 ? g.flowController.plan.bossHp : WYVERN.hp;
    b.rageAt = 0; b.phase2 = false; b.rage = 0; b.air = 0;
    this.setState(BossState.Intro);
    const e = g.emit(Ev.BossIntro); e.x = x; e.z = z; e.a = -1; e.b = BossKind.Wyvern;
    // 문이 닫히기 전에 다리에 남은 용병은 영웅 뒤 섬 안쪽으로 따라 들어온다(닫힌 문 밖에 갇히지 않게)
    const h = g.hero;
    for (let i = 0; i < MERC_COUNT; i++) {
      const m = this.state.mercs[i], inside = m.x > area.x0 + 1 && m.x < area.x1 - 1 && m.z > area.z0 + 1 && m.z < area.z1 - 1;
      if (inside && g.walkable(m.x, m.z, MERCS[i].radius)) continue;
      for (let k = 0; k < 12; k++) {
        const a = h.facing + Math.PI + (i - 1) * 0.7 + (k % 2 ? 1 : -1) * Math.floor(k / 2) * 0.35, r = 2 + (k % 3) * 0.6;
        const px = h.x + Math.sin(a) * r, pz = h.z + Math.cos(a) * r;
        if (pz > area.z1 - 1.5 || !g.walkable(px, pz, MERCS[i].radius)) continue;
        m.x = m.prevX = px; m.z = m.prevZ = pz; break;
      }
      if (!(m.z < area.z1 - 1.5)) { m.x = m.prevX = h.x + (i - 1) * 1.2; m.z = m.prevZ = h.z - 1.5; }
    }
  }

  copyPrevious(): void {
    if (!this.state.active) return;
    for (const m of this.state.mercs) { m.prevX = m.x; m.prevZ = m.z; }
  }

  // ── 위협 ───────────────────────────────────────
  /** 파티 칸의 위협 더하기(영웅 피해는 combat이, 용병 피해·치유는 여기서) */
  addThreat(slot: number, amount: number): void {
    if (!this.state.active || this.netMode || !(amount > 0)) return;
    this.threat[slot] += amount;
    if (slot > 0) this.state.mercs[slot - 1].threat = this.threat[slot];
  }

  private partyAlive(): void {
    const g = this.g;
    this.alive[0] = g.hero.hp > 0 && g.flow.scene === Scene.Dungeon;
    for (let i = 0; i < MERC_COUNT; i++) this.alive[i + 1] = this.state.mercs[i].alive;
  }

  private slotPos(slot: number): { x: number; z: number } {
    return slot <= 0 ? this.g.hero : this.state.mercs[slot - 1];
  }

  // ── 용병 ───────────────────────────────────────
  /** 세계 1스텝(용병·불 장판·도발 시계). 보스 두뇌는 stepBoss */
  step(rate: number, whole: boolean): void {
    const s = this.state, g = this.g;
    if (this.netMode || !s.active || rate <= 0 || g.flow.scene !== Scene.Dungeon) return;
    if (whole) {
      if (s.tauntTicks > 0 && --s.tauntTicks === 0) this.tauntBy = -1;
      this.stepFires();
      if (g.flow.scene !== Scene.Dungeon) return;
    }
    const b = g.boss, fight = b.state !== BossState.Inactive && b.state !== BossState.Dead;
    for (let i = 0; i < MERC_COUNT; i++) this.stepMerc(i, rate, whole, fight);
  }

  private stepMerc(i: number, rate: number, whole: boolean, fight: boolean): void {
    const g = this.g, s = this.state, m = s.mercs[i], d = MERCS[i], b = g.boss, h = g.hero;
    if (whole) {
      if (m.actTick < TIMER_MAX) m.actTick++;
      if (m.hurtTick < TIMER_MAX) m.hurtTick++;
    }
    if (m.down > 0) {
      if (whole && --m.down === 0) {
        m.hp = Math.max(1, Math.round(m.maxHp * MERC_REVIVE.hpRatio)); m.act = MercAct.Idle; m.actTick = 0;
        this.mercEvent(i, MercEvent.Revive, 0);
      }
      return;
    }
    if (whole) { if (m.hitClock > 0) m.hitClock--; if (m.skillClock > 0) m.skillClock--; }
    // 1) 위험(경고·불 장판) 확인 → 반응 지연 뒤 피하기
    const now = this.ptick;
    if (whole) {
      const danger = this.dangerAt(m.x, m.z, d.radius, i + 1);
      if (danger && m.dodgeAt < 0 && !m.evading) m.dodgeAt = now + this.rng.int(d.react[0], d.react[1] + 1);
      if (!danger && !m.evading) m.dodgeAt = -1;
      if (m.dodgeAt >= 0 && now >= m.dodgeAt && !m.evading) {
        m.dodgeAt = -1;
        if (this.safeSpot(m, d.radius, i + 1)) { m.evading = true; this.mercEvent(i, MercEvent.Dodge, 0); }
      }
    }
    if (m.evading) {
      const arrived = this.moveToward(m, m.evX, m.evZ, d.speed * 1.4, d.radius, rate, 0.3);
      m.act = MercAct.Move;
      if (arrived || (!this.dangerAt(m.x, m.z, d.radius, i + 1) && m.actTick > 8)) m.evading = false;
      return;
    }
    // 2) 역할 자리로 걷기
    if (!fight) { this.follow(m, d.radius, d.speed, rate, i); return; }
    const bx = b.x, bz = b.z, dist = Math.hypot(m.x - bx, m.z - bz) - b.radius;
    const want = this.rolePoint(i, d.keepMin, d.keepMax);
    const far = dist > d.keepMax || dist < d.keepMin;
    if (far) { this.moveToward(m, want.x, want.z, d.speed, d.radius, rate, 0.4); m.act = MercAct.Move; }
    else if (m.act === MercAct.Move) m.act = MercAct.Idle;
    m.facing = Math.atan2(bx - m.x, bz - m.z);
    if (!whole) return;
    // 3) 기술: 방패병 도발 · 사제 치유
    if (d.role === "tank" && m.skillClock === 0 && b.state !== BossState.Intro && !this.airborne && this.g.bossController.vulnerable) {
      m.skillClock = TAUNT.every; s.tauntTicks = TAUNT.ticks; this.tauntBy = i + 1; this.retarget = 0;
      m.act = MercAct.Cast; m.actTick = 0;
      this.mercEvent(i, MercEvent.Taunt, TAUNT.ticks);
    }
    if (d.role === "priest" && m.skillClock === 0) this.heal(i);
    // 4) 공격(사거리 안, 보스가 맞을 수 있을 때)
    if (m.hitClock === 0 && dist <= d.reach && g.bossController.vulnerable && b.hp > 0) {
      m.hitClock = d.hitEvery;
      m.act = MercAct.Attack; m.actTick = 0;
      const dmg = Math.max(1, Math.round(m.attack * d.hitMult * this.rng.range(0.9, 1.1)));
      const dealt = g.combat.allyHitBoss(dmg, m.x, m.z, i + 1);
      if (dealt > 0) this.addThreat(i + 1, dealt * d.threat);
      this.mercEvent(i, MercEvent.Attack, dealt);
    }
    if ((m.act === MercAct.Attack || m.act === MercAct.Cast) && m.actTick > 24) m.act = far ? MercAct.Move : MercAct.Idle;
    void h;
  }

  /** 전투 전·보스가 없을 때: 영웅 뒤를 따라 걷는다 */
  private follow(m: MercState, radius: number, speed: number, rate: number, i: number): void {
    const h = this.g.hero, a = h.facing + Math.PI + (i - 1) * 0.6, r = 2.4 + (i === 1 ? 0.6 : 0);
    const tx = h.x + Math.sin(a) * r, tz = h.z + Math.cos(a) * r;
    if (Math.hypot(m.x - tx, m.z - tz) > 1.2) { this.moveToward(m, tx, tz, speed, radius, rate, 0.6); m.act = MercAct.Move; }
    else { m.act = MercAct.Idle; m.facing = h.facing; }
  }

  /** 역할 자리: 보스 둘레 각도(방패 = 정면, 궁수·사제 = 좌·우 뒤쪽 비스듬히)와 거리 */
  private rolePoint(i: number, keepMin: number, keepMax: number): { x: number; z: number } {
    const b = this.g.boss, r = b.radius + (keepMin + keepMax) / 2;
    const spread = i === 0 ? 0 : i === 1 ? deg(120) : deg(-120);
    // 경고·불 장판 위면 각도를 30°씩 비틀어 안전한 자리를 찾는다(없으면 원래 자리)
    for (let k = 0; k < ROLE_TRIES.length; k++) {
      const a = b.facing + spread + ROLE_TRIES[k];
      let x = b.x + Math.sin(a) * r, z = b.z + Math.cos(a) * r;
      // 무대 밖이면 안쪽으로 당긴다
      const dx = x - this.cx, dz = z - this.cz, d = Math.hypot(dx, dz), lim = this.cr - ARENA_PAD - 1;
      if (d > lim) { x = this.cx + dx / d * lim; z = this.cz + dz / d * lim; }
      this._pt.x = x; this._pt.z = z;
      if (!this.dangerAt(x, z, 0.8)) break;
    }
    return this._pt;
  }
  private readonly _pt = { x: 0, z: 0 };

  /** 이동(축 따로, 벽에서 미끄러진다). 도착하면 true */
  private moveToward(m: MercState, tx: number, tz: number, speed: number, radius: number, rate: number, stopAt: number): boolean {
    const dx = tx - m.x, dz = tz - m.z, dist = Math.hypot(dx, dz);
    if (dist <= stopAt) return true;
    const step = Math.min(dist, speed * FIXED_DT * rate);
    const sx = dx / dist * step, sz = dz / dist * step;
    const g = this.g;
    if (g.walkable(m.x + sx, m.z, radius)) m.x += sx;
    if (g.walkable(m.x, m.z + sz, radius)) m.z += sz;
    m.facing = Math.atan2(dx, dz);
    return false;
  }

  /** 이 자리가 위험한가: 살아 있는 보스 경고 안이거나 불 장판 안 */
  private dangerAt(x: number, z: number, radius: number, self = -1): boolean {
    const g = this.g;
    for (let k = 0; k < this.slotCount; k++) {
      const slot = this.slots[k];
      if (slot < 0) continue;
      // 잿불 낙인: 내 낙인은 피할 수 없다(따라온다) — 대신 다른 파티원이 내 원을 피한다
      if (self >= 0 && this.brandWho[k] === self) continue;
      const t = g.telegraphs[slot];
      if (!t.active) continue;
      if (telegraphHit(t, x, z) || telegraphHit(t, x + radius, z) || telegraphHit(t, x - radius, z) || telegraphHit(t, x, z + radius) || telegraphHit(t, x, z - radius)) return true;
    }
    for (const f of this.state.fires) if (f.active && Math.hypot(f.x - x, f.z - z) < f.radius + radius) return true;
    return false;
  }

  /** 가장 가까운 안전한 자리(걸을 수 있고, 경고·불 밖, 무대 안). 찾으면 m.evX·evZ에 넣고 true */
  private safeSpot(m: MercState, radius: number, self = -1): boolean {
    const g = this.g, off = this.rng.next() * Math.PI * 2;
    for (const ring of SAFE_RINGS) {
      let best = -1, bestD = Infinity, bx = 0, bz = 0;
      for (let k = 0; k < SAFE_SAMPLES; k++) {
        const a = off + k * (Math.PI * 2 / SAFE_SAMPLES), x = m.x + Math.sin(a) * ring, z = m.z + Math.cos(a) * ring;
        if (Math.hypot(x - this.cx, z - this.cz) > this.cr - ARENA_PAD) continue;
        if (!g.walkable(x, z, radius)) continue;
        if (this.dangerAt(x, z, radius + 0.4, self)) continue;
        // 보스 몸 안은 피한다
        const db = Math.hypot(x - g.boss.x, z - g.boss.z);
        if (db < g.boss.radius + radius + 0.3) continue;
        const score = Math.abs(db - 6);
        if (score < bestD) { bestD = score; best = k; bx = x; bz = z; }
      }
      if (best >= 0) { m.evX = bx; m.evZ = bz; return true; }
    }
    return false;
  }

  /** 사제 치유: HP 비율이 가장 낮은 살아 있는 아군(영웅 포함, 사거리 안). 모두 92% 이상이면 0.5초 뒤 다시 본다 */
  private heal(i: number): void {
    const g = this.g, s = this.state, m = s.mercs[i], h = g.hero;
    let best = -1, ratio = 0.92;
    const hr = h.hp / Math.max(1, h.maxHp);
    if (h.hp > 0 && hr < ratio && Math.hypot(h.x - m.x, h.z - m.z) <= HEAL.range) { best = 0; ratio = hr; }
    for (let k = 0; k < MERC_COUNT; k++) {
      const o = s.mercs[k];
      if (!o.alive) continue;
      const r = o.hp / Math.max(1, o.maxHp);
      if (r < ratio && Math.hypot(o.x - m.x, o.z - m.z) <= HEAL.range) { best = k + 1; ratio = r; }
    }
    if (best < 0) { m.skillClock = 30; return; }
    m.skillClock = HEAL.every; m.act = MercAct.Cast; m.actTick = 0;
    let amount: number;
    if (best === 0) {
      amount = Math.min(h.maxHp - h.hp, Math.round(h.maxHp * HEAL.ratio));
      h.hp += amount;
      const e = g.emit(Ev.HeroHeal); e.x = h.x; e.z = h.z; e.a = amount; e.b = 2;
    } else {
      const o = s.mercs[best - 1];
      amount = Math.min(o.maxHp - o.hp, Math.round(o.maxHp * HEAL.ratio));
      o.hp += amount;
    }
    this.addThreat(i + 1, amount * HEAL.threat);
    this.mercEvent(i, MercEvent.Heal, best, amount);
  }

  /** 용병 피격(받는 피해 배율 적용, 정수). 쓰러지면 다시 일어나기 시계를 건다 */
  private mercHurt(i: number, damage: number, x: number, z: number): void {
    const m = this.state.mercs[i];
    if (!m.alive) return;
    const dmg = Math.max(1, Math.round(damage * MERCS[i].damageTaken));
    m.hp = Math.max(0, m.hp - dmg); m.hurtTick = 0;
    const e = this.g.emit(Ev.Hit); e.x = m.x; e.y = 1; e.z = m.z; e.a = dmg; e.b = 3; e.c = i; e.d = Math.atan2(m.x - x, m.z - z); e.flags = HitFlag.Ally;
    if (m.hp === 0) {
      m.down = MERC_REVIVE.ticks; m.act = MercAct.Down; m.actTick = 0; m.evading = false; m.dodgeAt = -1;
      if (this.tauntBy === i + 1) { this.tauntBy = -1; this.state.tauntTicks = 0; }
      this.mercEvent(i, MercEvent.Down, MERC_REVIVE.ticks);
    } else this.mercEvent(i, MercEvent.Hurt, dmg);
  }

  private mercEvent(i: number, kind: number, a: number, c = 0): void {
    const m = this.state.mercs[i], e = this.g.emit(Ev.RaidMerc);
    e.x = m.x; e.z = m.z; e.a = i; e.b = kind; e.c = a; e.d = c;
  }

  // ── 불 장판 ────────────────────────────────────
  private spawnFire(x: number, z: number): void {
    const F = WYVERN.fire;
    let pick: FirePool | null = null;
    for (const f of this.state.fires) if (!f.active) { pick = f; break; }
    if (!pick) { pick = this.state.fires[0]; for (const f of this.state.fires) if (f.ttl < pick.ttl) pick = f; }
    pick.active = true; pick.x = x; pick.z = z; pick.radius = F.radius; pick.ttl = pick.total = F.ticks; pick.clock = F.every;
    const e = this.g.emit(Ev.RaidFire); e.x = x; e.z = z; e.a = F.radius; e.b = F.ticks;
  }

  private stepFires(): void {
    const g = this.g, h = g.hero, F = WYVERN.fire, dmg = this.tune.damage * F.dmg * this.dmgMult;
    for (const f of this.state.fires) {
      if (!f.active) continue;
      if (--f.ttl <= 0) { f.active = false; continue; }
      if (--f.clock > 0) continue;
      f.clock = F.every;
      if (h.hp > 0 && Math.hypot(h.x - f.x, h.z - f.z) < f.radius) { g.combat.burnHero(Math.round(dmg), 0); if (g.flow.scene !== Scene.Dungeon) return; }
      for (let i = 0; i < MERC_COUNT; i++) {
        const m = this.state.mercs[i];
        if (m.alive && Math.hypot(m.x - f.x, m.z - f.z) < f.radius) this.mercHurt(i, dmg, f.x, f.z);
      }
    }
  }

  // ── 비룡 ───────────────────────────────────────
  private get dmgMult(): number { return this.state.enraged ? WYVERN.enrage.dmgMult : 1; }

  private setState(state: BossStateId): void {
    const b = this.g.boss;
    b.state = state; b.stateTick = 0;
    b.invulnerable = state === BossState.Intro || state === BossState.Dying || state === BossState.Dead || this.state.air > 0;
  }

  private cancelPattern(): void {
    for (let k = 0; k < SLOTS; k++) { this.g.releaseTelegraph(this.slots[k]); this.slots[k] = -1; }
    this.slotCount = 0; this.resolved = 0; this.hitMask = 0; this.dashLeft = 0;
    this.brandWho.fill(-1); this.state.brandSlots = 0; this.state.brandLeft = 0;
    this.state.pattern = -1;
  }

  private tele(k: number, shape: TeleShapeId, x: number, z: number, radius: number, angle: number, width: number, total: number, mult: number): boolean {
    const g = this.g, slot = g.allocateTelegraph(TeleSource.Boss, BossKind.Wyvern);
    if (slot < 0) return false;
    const t = g.telegraphs[slot];
    t.shape = shape; t.x = x; t.z = z; t.radius = radius; t.angle = angle; t.width = width; t.tick = 0; t.total = total;
    this.slots[k] = slot; this.born[k] = this.ptick; this.due[k] = this.ptick + total; this.mult[k] = mult;
    if (k + 1 > this.slotCount) this.slotCount = k + 1;
    return true;
  }

  private pattern(id: number): void {
    this.announce(id);
    this.state.pattern = id;
  }

  /** 패턴 알림만(페이즈 2·광폭 진입): 진행 중인 공격 패턴 번호는 그대로 둔다(Codex P2 #207 — 덮어쓰면 장판이 불을 안 남기고 돌진이 달리지 않았다) */
  private announce(id: number): void {
    const g = this.g, b = g.boss, e = g.emit(Ev.BossPattern);
    e.x = b.x; e.z = b.z; e.a = id; e.b = BossKind.Wyvern; e.c = 0;
  }

  /** 보스 두뇌(BossController.step이 넘긴다). 세계 시간 1스텝. 비행 높이는 보스 뷰에도 옮긴다(렌더·HUD) */
  stepBoss(rate: number, whole: boolean): void {
    if (this.netMode) return; // 넷 판: 보스 상태·비행 높이는 raidLink가 서버 스냅으로 채운다
    this.stepBossInner(rate, whole);
    this.g.boss.air = this.state.air;
  }

  private stepBossInner(rate: number, whole: boolean): void {
    const g = this.g, b = g.boss, s = this.state;
    if (!s.active || rate <= 0) return;
    if (whole) {
      this.ptick++;
      if (b.stateTick < TIMER_MAX) b.stateTick++;
      if (b.hitTick < TIMER_MAX) b.hitTick++;
      if (this.cooldown > 0) this.cooldown--;
    }
    const W = WYVERN;
    if (b.state === BossState.Intro) {
      if (b.stateTick >= W.intro) { this.setState(BossState.Chase); this.cooldown = 30; }
      return;
    }
    if (b.state === BossState.Dying) {
      if (b.stateTick >= W.dying) { this.setState(BossState.Dead); g.flowController.requestClear(); }
      return;
    }
    if (b.state === BossState.Dead) return;
    // 광폭 시계(등장 뒤부터)
    if (whole && !s.enraged && s.enrageIn > 0 && --s.enrageIn === 0) { s.enraged = true; this.announce(WyvernPattern.enrage); }
    // 비행 주기(RAID-PRD 5.2 25초)는 지상에 있는 세계 시간으로 센다: 공격·경고·경직 중에도 흐른다(Codex P2 #207 — 쫓는 시간만 세면 비행이 크게 늦어졌다)
    if (whole && s.phase === 2 && this.flight === 0 && this.flightClock > 0) this.flightClock--;
    if (b.state === BossState.Stagger) { if (b.stateTick >= W.staggerTicks) this.setState(BossState.Chase); return; }
    // 대상 고르기
    this.partyAlive();
    if (whole && (--this.retarget <= 0 || !this.alive[Math.max(0, s.target)])) {
      this.retarget = W.retargetEvery;
      s.target = pickTarget(s.target, this.alive, this.threat, this.tauntBy, W.retargetMargin);
    }
    if (this.flight > 0) { this.stepFlight(rate, whole); return; }
    if (this.slotCount > 0) { this.stepPattern(whole); return; }
    if (b.state === BossState.Slam || b.state === BossState.Sweep) {
      if (this.dashLeft > 0) { this.stepDash(rate); return; }
      if (b.stateTick >= 21) this.setState(BossState.Chase);
      return;
    }
    const t = s.target >= 0 ? this.slotPos(s.target) : g.hero;
    const dx = t.x - b.x, dz = t.z - b.z, dist = Math.hypot(dx, dz);
    if (dist > 1e-6) b.facing = turn(b.facing, Math.atan2(dx, dz), 0.09 * rate);
    if (this.cooldown === 0 && this.begin(dist)) return;
    if (b.state !== BossState.Chase) this.setState(BossState.Chase);
    if (dist > b.radius + W.bite.reach * 0.8) {
      const step = W.speed * FIXED_DT * rate, sx = dx / dist * step, sz = dz / dist * step;
      if (g.walkableBoss(b.x + sx, b.z, b.radius)) b.x += sx;
      if (g.walkableBoss(b.x, b.z + sz, b.radius)) b.z += sz;
    }
  }

  /** 다음 패턴 고르기(지상). 시작하면 true */
  private begin(dist: number): boolean {
    const g = this.g, s = this.state, b = g.boss, W = WYVERN;
    if (s.phase === 2 && this.flightClock === 0 && this.groundLeft <= 0) return this.takeoff();
    // 뒤에 둘 이상 서 있으면 꼬리
    let behind = 0;
    for (let k = 0; k < PARTY_SIZE; k++) {
      if (!this.alive[k]) continue;
      const p = this.slotPos(k), d = Math.hypot(p.x - b.x, p.z - b.z);
      if (d > W.tail.range + b.radius * 0.3) continue;
      const a = Math.abs(angleDiff(Math.atan2(p.x - b.x, p.z - b.z), b.facing));
      if (a > deg(110)) behind++;
    }
    let kind: number;
    const B = this.tune.brand;
    // 하드 전용 잿불 낙인(C13): 지상 공격 every번마다(꼬리보다 먼저 — 산개를 강제한다)
    if (B && this.attacks % B.every === B.every - 1 && this.lastPattern !== WyvernPattern.brand) kind = WyvernPattern.brand;
    else if (behind >= 2 && this.lastPattern !== WyvernPattern.tail) kind = WyvernPattern.tail;
    else {
      const seq = s.phase === 2 ? P2_SEQ : P1_SEQ;
      kind = seq[this.attacks % seq.length];
      if (kind === WyvernPattern.bite && dist > b.radius + W.bite.reach) {
        if (dist > b.radius + 9) kind = WyvernPattern.charge;
        else return false; // 더 다가간다
      }
    }
    const ok = kind === WyvernPattern.bite ? this.startCone(WyvernPattern.bite, b.facing, W.bite.range, W.bite.arcDeg, W.bite.windup, W.bite.dmg, BossState.SlamWindup)
      : kind === WyvernPattern.tail ? this.startCone(WyvernPattern.tail, b.facing + Math.PI, W.tail.range, W.tail.arcDeg, W.tail.windup, W.tail.dmg, BossState.SweepWindup)
      : kind === WyvernPattern.pools ? this.startPools()
      : kind === WyvernPattern.brand ? this.startBrand()
      : this.startCharge();
    if (!ok) return false;
    this.attacks++; this.lastPattern = kind;
    if (s.phase === 2) this.groundLeft--;
    return true;
  }

  private startCone(id: number, angle: number, range: number, arcDeg: number, windup: number, mult: number, state: BossStateId): boolean {
    const b = this.g.boss;
    this.cancelPattern();
    if (!this.tele(0, TeleShape.Cone, b.x, b.z, range + b.radius * 0.5, angle, deg(arcDeg), windup, mult)) return false;
    this.setState(state); this.pattern(id);
    return true;
  }

  private startPools(): boolean {
    const g = this.g, s = this.state, W = WYVERN, b = g.boss;
    this.cancelPattern();
    let k = 0;
    for (let p = 0; p < PARTY_SIZE && k < SLOTS; p++) {
      if (!this.alive[p]) continue;
      const pos = this.slotPos(p);
      if (this.tele(k, TeleShape.Circle, pos.x, pos.z, W.pools.radius, 0, 0, W.pools.windup, W.pools.dmg)) k++;
    }
    const extra = (s.phase === 2 ? W.pools.extraP2 : 0) + this.tune.poolsExtraP1;
    for (let e = 0; e < extra && k < SLOTS; e++) {
      const a = this.rng.next() * Math.PI * 2, r = this.rng.range(3, Math.min(W.pools.scatter, this.cr - 3));
      if (this.tele(k, TeleShape.Circle, this.cx + Math.sin(a) * r, this.cz + Math.cos(a) * r, W.pools.radius, 0, 0, W.pools.windup, W.pools.dmg)) k++;
    }
    if (k === 0) return false;
    this.setState(BossState.Cast); this.pattern(WyvernPattern.pools);
    void b;
    return true;
  }

  /**
   * 잿불 낙인(하드 전용): 지금 대상 + 무작위 다른 파티원(marks명)의 발밑에 따라다니는 원. windup 뒤 터진다 —
   * 주인은 bearerDmg, 원 안에 같이 선 다른 파티원은 crowdDmg. 터진 자리에 불 장판
   */
  private startBrand(): boolean {
    const B = this.tune.brand;
    if (!B) return false;
    const s = this.state;
    this.cancelPattern();
    let k = 0, mask = 0;
    const first = s.target >= 0 && this.alive[s.target] ? s.target : this.randomAlive();
    for (let tries = 0; k < B.marks && tries < 8; tries++) {
      const who = k === 0 ? first : this.randomAlive();
      if (who < 0 || (mask & (1 << who))) continue;
      const p = this.slotPos(who);
      if (!this.tele(k, TeleShape.Circle, p.x, p.z, B.radius, 0, 0, B.windup, B.bearerDmg)) break;
      this.brandWho[k] = who; mask |= 1 << who; k++;
    }
    if (k === 0) return false;
    s.brandSlots = mask; s.brandLeft = B.windup;
    this.setState(BossState.Cast); this.pattern(WyvernPattern.brand);
    const e = this.g.emit(Ev.BossPattern); e.x = this.g.boss.x; e.z = this.g.boss.z; e.a = WyvernPattern.brand; e.b = BossKind.Wyvern; e.c = mask;
    return true;
  }

  /** 낙인 k 폭발: 주인 bearerDmg, 원 안의 다른 파티원 crowdDmg(무겁다), 자리에 불 장판 */
  private brandStrike(k: number): void {
    const B = this.tune.brand!, g = this.g, h = g.hero, slot = this.slots[k], t = g.telegraphs[slot], who = this.brandWho[k];
    const mul = this.tune.damage * this.dmgMult;
    // 같은 틱에 터지는 낙인 피해는 모아서 한 번에 준다 — 첫 타의 피격 무적이 둘째(원 안 ×1.6)를 지우면 겹쳐 서도 벌이 없다(#336 Codex P2)
    if (h.hp > 0 && (who === 0 || telegraphHit(t, h.x, h.z))) {
      if (this.brandHeroDmg === 0) { this.brandHeroX = t.x; this.brandHeroZ = t.z; }
      this.brandHeroDmg += mul * (who === 0 ? B.bearerDmg : B.crowdDmg);
      if (who !== 0) this.brandHeroHeavy = true;
    }
    for (let i = 0; i < MERC_COUNT; i++) {
      const m = this.state.mercs[i];
      if (m.alive && (who === i + 1 || telegraphHit(t, m.x, m.z))) this.mercHurt(i, mul * (who === i + 1 ? B.bearerDmg : B.crowdDmg), t.x, t.z);
    }
    this.spawnFire(t.x, t.z);
    const e = g.emit(Ev.BossPattern); e.x = t.x; e.z = t.z; e.a = WyvernPattern.brand; e.b = BossKind.Wyvern; e.c = -1; e.d = who;
  }

  private startCharge(): boolean {
    const g = this.g, b = g.boss, W = WYVERN, s = this.state;
    const t = s.target >= 0 ? this.slotPos(s.target) : g.hero;
    const a = Math.atan2(t.x - b.x, t.z - b.z);
    this.cancelPattern();
    if (!this.tele(0, TeleShape.Line, b.x, b.z, W.charge.length, a, W.charge.width, W.charge.windup, W.charge.dmg)) return false;
    b.facing = a; this.dashAngle = a;
    this.setState(BossState.SweepWindup); this.pattern(WyvernPattern.charge);
    return true;
  }

  /** 지상 패턴 진행: 경고를 채우고 판정 틱에 터뜨린다. 다 끝나면 친 뒤 상태로 */
  private stepPattern(whole: boolean): void {
    const g = this.g, b = g.boss;
    void whole;
    let left = 0;
    for (let k = 0; k < this.slotCount; k++) {
      const slot = this.slots[k];
      if (slot < 0) continue;
      const t = g.telegraphs[slot];
      const id = this.state.pattern;
      // 잿불 낙인은 주인을 따라다닌다(쓰러지면 그 자리에 남는다)
      if (id === WyvernPattern.brand && this.brandWho[k] >= 0 && this.alive[this.brandWho[k]]) { const p = this.slotPos(this.brandWho[k]); t.x = p.x; t.z = p.z; }
      t.tick = Math.min(t.total, this.ptick - this.born[k] + g.worldFrac);
      if (this.ptick < this.due[k]) { left++; continue; }
      if (id === WyvernPattern.brand) { this.brandStrike(k); g.fireTelegraph(slot); this.slots[k] = -1; this.brandWho[k] = -1; continue; }
      this.strike(k, id === WyvernPattern.charge || id === WyvernPattern.bite || id === WyvernPattern.land);
      if (g.flow.scene !== Scene.Dungeon) return;
      if (id === WyvernPattern.pools) this.spawnFire(t.x, t.z);
      g.fireTelegraph(slot); this.slots[k] = -1;
    }
    if (this.brandHeroDmg > 0) {
      const d = this.brandHeroDmg, heavy = this.brandHeroHeavy;
      this.brandHeroDmg = 0; this.brandHeroHeavy = false;
      g.combat.hurtHero(d, this.brandHeroX, this.brandHeroZ, heavy, 1);
      if (g.flow.scene !== Scene.Dungeon) return;
    }
    if (this.state.pattern === WyvernPattern.brand) this.state.brandLeft = left > 0 ? Math.max(0, this.due[0] - this.ptick) : 0;
    if (left > 0) return;
    const id = this.state.pattern;
    this.slotCount = 0; this.hitMask = 0; this.state.brandSlots = 0; this.state.brandLeft = 0;
    this.setState(id === WyvernPattern.tail || id === WyvernPattern.pools || id === WyvernPattern.brand ? BossState.Sweep : BossState.Slam);
    if (id === WyvernPattern.charge) this.dashLeft = WYVERN.charge.length - b.radius * 0.5;
    this.cooldown = Math.round(WYVERN.cooldown * this.tune.cooldownMult * (this.state.enraged ? WYVERN.enrage.cooldownMult : 1));
    this.state.pattern = -1;
  }

  /** 경고 k 판정: 그 모양 안의 파티원(이번 패턴에 아직 안 맞은)에게 피해 */
  private strike(k: number, heavy: boolean): void {
    const g = this.g, h = g.hero, b = g.boss, slot = this.slots[k], t = g.telegraphs[slot];
    const dmg = this.tune.damage * this.mult[k] * this.dmgMult;
    if (!(this.hitMask & 1) && h.hp > 0 && telegraphHit(t, h.x, h.z)) {
      this.hitMask |= 1;
      g.combat.hurtHero(dmg, b.x, b.z, heavy, 1);
      if (g.flow.scene !== Scene.Dungeon) return;
    }
    for (let i = 0; i < MERC_COUNT; i++) {
      const m = this.state.mercs[i], bit = 2 << i;
      if (!(this.hitMask & bit) && m.alive && telegraphHit(t, m.x, m.z)) { this.hitMask |= bit; this.mercHurt(i, dmg, b.x, b.z); }
    }
  }

  private stepDash(rate: number): void {
    const g = this.g, b = g.boss, C = WYVERN.charge;
    const step = Math.min(this.dashLeft, (C.length / C.dashTicks) * rate);
    const sx = Math.sin(this.dashAngle) * step, sz = Math.cos(this.dashAngle) * step;
    let moved = false;
    if (g.walkableBoss(b.x + sx, b.z, b.radius)) { b.x += sx; moved = true; }
    if (g.walkableBoss(b.x, b.z + sz, b.radius)) { b.z += sz; moved = true; }
    this.dashLeft = moved ? this.dashLeft - step : 0;
    if (this.dashLeft <= 1e-3 || b.stateTick >= C.dashTicks + 21) { this.dashLeft = 0; this.setState(BossState.Chase); }
  }

  // ── 비행(페이즈 2) ─────────────────────────────
  private takeoff(): boolean {
    const F = WYVERN.flight;
    this.cancelPattern();
    this.flight = 1; this.breathMade = 0; this.state.air = 0.001;
    this.setState(BossState.Cast); this.pattern(WyvernPattern.takeoff);
    this.flightClock = F.every; this.groundLeft = F.groundAttacks;
    return true;
  }

  private stepFlight(rate: number, whole: boolean): void {
    const g = this.g, b = g.boss, s = this.state, F = WYVERN.flight;
    void rate;
    if (this.flight === 1) {
      s.air = Math.min(1, b.stateTick / F.takeoff + 0.001);
      if (b.stateTick >= F.takeoff) { this.flight = 2; this.breathMade = 0; b.stateTick = 0; }
      return;
    }
    if (this.flight === 2) {
      // 브레스: breathGap마다 한 줄씩 깐다(살아 있는 파티원 자리를 지나는 무작위 방향)
      if (this.breathMade < F.breaths && b.stateTick >= this.breathMade * F.breathGap) {
        const pick = this.randomAlive(), p = this.slotPos(pick < 0 ? 0 : pick);
        const a = this.rng.next() * Math.PI, half = F.breathLength / 2;
        const x0 = p.x - Math.sin(a) * half, z0 = p.z - Math.cos(a) * half;
        if (this.tele(this.breathMade, TeleShape.Line, x0, z0, F.breathLength, a, F.breathWidth, F.breathWindup, F.breathDmg)) {
          this.pattern(WyvernPattern.breath);
          // 비룡은 줄 위로 날아 들어간다(그림자·몸이 줄을 따라간다)
          b.facing = a;
        }
        this.breathMade++;
      }
      let left = this.breathMade < F.breaths ? 1 : 0;
      for (let k = 0; k < this.slotCount; k++) {
        const slot = this.slots[k];
        if (slot < 0) continue;
        const t = g.telegraphs[slot];
        t.tick = Math.min(t.total, this.ptick - this.born[k] + g.worldFrac);
        if (this.ptick < this.due[k]) { left++; continue; }
        this.hitMask = 0;
        this.strike(k, false);
        if (g.flow.scene !== Scene.Dungeon) return;
        // 브레스가 지나간 줄 가운데에 남는 불은 없다(장판은 화염 장판 패턴만)
        g.fireTelegraph(slot); this.slots[k] = -1;
        // 몸은 줄 끝으로
        this.toward(t.x + Math.sin(t.angle) * t.radius * 0.5, t.z + Math.cos(t.angle) * t.radius * 0.5, this.cr - 4);
      }
      if (left > 0) return;
      this.slotCount = 0;
      // 착지 경고: 대상 자리 큰 원
      const t = s.target >= 0 ? this.slotPos(s.target) : g.hero;
      this.landX = t.x; this.landZ = t.z;
      this.flight = 3; b.stateTick = 0; this.hitMask = 0;
      this.tele(0, TeleShape.Circle, this.landX, this.landZ, F.landRadius, 0, 0, F.landWindup, F.landDmg);
      this.pattern(WyvernPattern.land);
      return;
    }
    // 착지: 경고가 차는 동안 내려와 원 가운데로 옮겨 간다
    const slot = this.slots[0];
    const k = Math.min(1, b.stateTick / F.landWindup);
    s.air = Math.max(0.001, 1 - k);
    b.x += (this.landX - b.x) * Math.min(1, 0.08 * rate);
    b.z += (this.landZ - b.z) * Math.min(1, 0.08 * rate);
    if (slot >= 0) g.telegraphs[slot].tick = Math.min(F.landWindup, b.stateTick + g.worldFrac);
    if (b.stateTick < F.landWindup || !whole) return;
    // 착지점이 걸을 수 없는 곳이면 무대 중심 쪽으로 당긴다
    if (!g.walkableBoss(this.landX, this.landZ, b.radius)) { this.landX = this.cx; this.landZ = this.cz; }
    b.x = this.landX; b.z = this.landZ;
    s.air = 0;
    this.hitMask = 0;
    if (slot >= 0) { this.strike(0, true); if (g.flow.scene !== Scene.Dungeon) return; g.fireTelegraph(slot); this.slots[0] = -1; }
    this.slotCount = 0; this.flight = 0;
    this.setState(BossState.Slam);
    this.cooldown = Math.round(WYVERN.cooldown * this.tune.cooldownMult); this.state.pattern = -1;
  }

  /** 공중 비룡 몸을 (x,z)로 옮긴다(무대 중심 반경 lim 안으로 자른다 — 공중이라 벽 판정 없이) */
  private toward(x: number, z: number, lim: number): void {
    const b = this.g.boss, dx = x - this.cx, dz = z - this.cz, d = Math.hypot(dx, dz);
    if (d > lim && d > 0) { x = this.cx + dx / d * lim; z = this.cz + dz / d * lim; }
    b.x = x; b.z = z;
  }

  private randomAlive(): number {
    let n = 0;
    for (let k = 0; k < PARTY_SIZE; k++) if (this.alive[k]) n++;
    if (n === 0) return -1;
    let r = this.rng.int(0, n);
    for (let k = 0; k < PARTY_SIZE; k++) if (this.alive[k] && r-- === 0) return k;
    return 0;
  }

  /** 보스가 피해를 받았다(영웅·용병 공통, 실제로 줄어든 HP). 페이즈 2·경직·쓰러짐 */
  bossDamaged(amount: number): void {
    if (this.netMode) return; // 넷 판: 페이즈·경직·쓰러짐은 서버 판정(boss 사건)
    const g = this.g, b = g.boss, s = this.state;
    if (b.hp <= 0) {
      if (b.state === BossState.Dying || b.state === BossState.Dead) return;
      this.cancelPattern(); this.flight = 0; s.air = 0;
      this.setState(BossState.Dying);
      for (const f of s.fires) f.active = false;
      const e = g.emit(Ev.BossDefeated); e.x = b.x; e.z = b.z; e.a = -1; e.b = BossKind.Wyvern;
      return;
    }
    if (s.phase === 1 && b.hp <= b.maxHp * WYVERN.flight.phase) {
      s.phase = 2; b.phase2 = true; this.flightClock = WYVERN.flight.firstDelay; this.groundLeft = 0;
      this.announce(WyvernPattern.phase2);
    }
    if (this.airborne || b.state === BossState.Stagger || this.flight > 0) return;
    this.staggerAcc += amount;
    const threshold = b.maxHp * WYVERN.staggerRatio;
    b.stagger = Math.min(1, this.staggerAcc / threshold);
    if (this.staggerAcc >= threshold) {
      this.staggerAcc -= threshold;
      this.cancelPattern();
      this.setState(BossState.Stagger);
      b.stagger = 1;
      const e = g.emit(Ev.GuardBreak); e.x = b.x; e.z = b.z; e.a = 1; e.b = 0; e.c = WYVERN.staggerTicks;
    }
  }

}

/** 역할 자리 각도 비틀기 순서(라디안): 그대로 → ±30° → ±60° → ±90° */
const ROLE_TRIES = [0, deg(30), deg(-30), deg(60), deg(-60), deg(90), deg(-90)] as const;
const P1_SEQ = [WyvernPattern.bite, WyvernPattern.pools, WyvernPattern.bite, WyvernPattern.charge] as const;
const P2_SEQ = [WyvernPattern.pools, WyvernPattern.bite, WyvernPattern.charge, WyvernPattern.bite] as const;

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
function turn(from: number, to: number, max: number): number {
  const d = angleDiff(to, from);
  return from + Math.max(-max, Math.min(max, d));
}
